import { useState, useEffect } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { 
  Building2, 
  FileText, 
  ShoppingCart, 
  Users, 
  BarChart3,
  Clock,
  CheckCircle,
  TrendingUp,
  DollarSign
} from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import { getUserType } from '../../utils/userPermissions'
import BuyerQuoteDashboard from '../../components/BuyerQuoteDashboard'
import VendorQuoteDashboard from '../../components/VendorQuoteDashboard'

interface DashboardStats {
  activeRfqs: number
  totalQuotes: number
  pendingOrders: number
  completedProjects: number
  totalSpent: number
  avgResponseTime: number
}

export default function EnterpriseDashboardPage() {
  const { user } = useAuthStore()
  const userType = getUserType(user)
  const [stats, setStats] = useState<DashboardStats>({
    activeRfqs: 0,
    totalQuotes: 0,
    pendingOrders: 0,
    completedProjects: 0,
    totalSpent: 0,
    avgResponseTime: 0
  })
  const [loading, setLoading] = useState(true)

  // Access control
  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (userType !== 'ENTERPRISE_BUYER' && userType !== 'ENTERPRISE_VENDOR') {
    return <Navigate to="/" replace />
  }

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      // This would be replaced with actual API calls
      // For now, using mock data
      setStats({
        activeRfqs: 5,
        totalQuotes: 23,
        pendingOrders: 3,
        completedProjects: 12,
        totalSpent: 150000,
        avgResponseTime: 2.5
      })
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Enterprise Dashboard</h1>
          <p className="mt-2 text-gray-600">
            Welcome back, {user?.companyName || user?.firstName}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
              </div>
              <div className="ml-3 sm:ml-4 flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Active RFQs</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.activeRfqs}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
              </div>
              <div className="ml-3 sm:ml-4 flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Total Quotes</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.totalQuotes}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ShoppingCart className="h-6 w-6 sm:h-8 sm:w-8 text-orange-600" />
              </div>
              <div className="ml-3 sm:ml-4 flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Pending Orders</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.pendingOrders}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DollarSign className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
              </div>
              <div className="ml-3 sm:ml-4 flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Total Spent</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900 break-words">
                  ${stats.totalSpent.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">Quick Actions</h2>
            </div>
            <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
              {userType === 'ENTERPRISE_BUYER' && (
                <>
                  <Link
                    to="/enterprise/rfqs"
                    className="flex items-center p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 mr-3 sm:mr-4 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 text-sm sm:text-base truncate">Manage RFQs</h3>
                      <p className="text-xs sm:text-sm text-gray-500 truncate">Create and manage your requests for quotes</p>
                    </div>
                  </Link>

                  <Link
                    to="/enterprise"
                    className="flex items-center p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Users className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 mr-3 sm:mr-4 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 text-sm sm:text-base truncate">Browse Vendors</h3>
                      <p className="text-xs sm:text-sm text-gray-500 truncate">Find and connect with qualified vendors</p>
                    </div>
                  </Link>
                </>
              )}

              {userType === 'ENTERPRISE_VENDOR' && (
                <>
                  <Link
                    to="/enterprise/vendor-rfqs"
                    className="flex items-center p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 mr-3 sm:mr-4 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 text-sm sm:text-base truncate">View RFQ Opportunities</h3>
                      <p className="text-xs sm:text-sm text-gray-500 truncate">Respond to requests for quotes</p>
                    </div>
                  </Link>

                  {user?.isGovCertified && (
                    <Link
                      to="/enterprise/gov-rfqs"
                      className="flex items-center p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 mr-3 sm:mr-4 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 text-sm sm:text-base truncate">Government RFQs</h3>
                        <p className="text-xs sm:text-sm text-gray-500 truncate">Browse government opportunities</p>
                      </div>
                    </Link>
                  )}
                </>
              )}

              <Link
                to="/enterprise/post-quote-activity"
                className="flex items-center p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600 mr-3 sm:mr-4 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 text-sm sm:text-base truncate">Post-Quote Activity Manager</h3>
                  <p className="text-xs sm:text-sm text-gray-500 truncate">Manage orders, agreements, and fulfillments</p>
                </div>
              </Link>

              {/* Quick link to Quotes section */}
              <button
                onClick={() => {
                  const quotesSection = document.getElementById('quotes-section');
                  quotesSection?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="flex items-center p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-600 mr-3 sm:mr-4 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 text-sm sm:text-base truncate">My Quotes</h3>
                  <p className="text-xs sm:text-sm text-gray-500 truncate">
                    {userType === 'ENTERPRISE_BUYER' ? 'View quote requests and responses' : 'View and respond to quotes'}
                  </p>
                </div>
              </button>
            </div>
                  </div>

        {/* Post-Quote Activity Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8 min-h-[500px]">
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Post-Quote Activity</h2>
            <p className="text-sm text-gray-600 mt-1">
              Manage purchase orders, agreements, fulfillment tracking, invoicing, and vendor feedback
            </p>
          </div>
          <div className="p-4 sm:p-6 min-h-[400px]">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center space-x-3">
                  <ShoppingCart className="h-6 w-6 text-blue-600" />
                  <div>
                    <h3 className="font-medium text-blue-900">Purchase Orders</h3>
                    <p className="text-sm text-blue-700">Create and manage POs</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex items-center space-x-3">
                  <FileText className="h-6 w-6 text-green-600" />
                  <div>
                    <h3 className="font-medium text-green-900">Digital Agreements</h3>
                    <p className="text-sm text-green-700">Upload contracts & NDAs</p>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <div className="flex items-center space-x-3">
                  <Clock className="h-6 w-6 text-orange-600" />
                  <div>
                    <h3 className="font-medium text-orange-900">Fulfillment Tracking</h3>
                    <p className="text-sm text-orange-700">Track deliveries</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <Link
                to="/enterprise/post-quote-activity"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Access Full Post-Quote Activity Manager →
              </Link>
            </div>
          </div>
        </div>

        {/* My Quotes Section */}
        <div id="quotes-section" className="bg-white rounded-lg shadow mb-8 min-h-[500px]">
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">My Quotes</h2>
            <p className="text-sm text-gray-600 mt-1">
              {userType === 'ENTERPRISE_BUYER'
                ? 'Manage your quote requests and responses'
                : 'View and respond to quote requests'
              }
            </p>
          </div>
          <div className="p-4 sm:p-6 min-h-[400px]">
            {userType === 'ENTERPRISE_BUYER' ? (
              <BuyerQuoteDashboard hideHeader={true} />
            ) : (
              <VendorQuoteDashboard hideHeader={true} />
            )}
          </div>
        </div>

        {/* Recent Activity */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">Recent Activity</h2>
            </div>
            <div className="p-4 sm:p-6">
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-500" />
                  </div>
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="text-xs sm:text-sm text-gray-900 truncate">RFQ #12345 received 3 new responses</p>
                    <p className="text-xs text-gray-500">2 hours ago</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500" />
                  </div>
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="text-xs sm:text-sm text-gray-900 truncate">Purchase Order #PO-001 is pending approval</p>
                    <p className="text-xs text-gray-500">1 day ago</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
                  </div>
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="text-xs sm:text-sm text-gray-900 truncate">New vendor registered: Solar Solutions Inc.</p>
                    <p className="text-xs text-gray-500">3 days ago</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}